'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '@/lib/auth';
import {
  getAccessibleFeatures,
  invalidateAccessibleFeaturesCache,
  parseUpgradeCta,
  type UpgradeCta,
  type MarketplaceVisibility,
} from '@/lib/productization/productization-service';

const FEATURES_TIMING = typeof window !== 'undefined' && process.env.NODE_ENV === 'development';
function logFeaturesTiming(label: string) {
  if (FEATURES_TIMING) {
    const ts = typeof performance !== 'undefined' ? performance.now().toFixed(0) : Date.now();
    console.log(`[features] ${label} @ ${ts}ms`);
  }
}

export interface UseAccessibleFeaturesResult {
  accessibleFeatures: Set<string>;
  upgradeCta: UpgradeCta;
  marketplaceVisibility: MarketplaceVisibility | string;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  hasFeature: (featureId: string) => boolean;
  hasAnyFeature: (featureIds: string[]) => boolean;
}

const DEFAULT_UPGRADE_CTA: UpgradeCta = { url: null, label: 'Contact Sales', email: null };

function readE2eAccessibleFeaturesBootstrap(): {
  features: Set<string>;
  tenantId: string | null;
  ready: boolean;
} {
  if (typeof window === 'undefined') {
    return { features: new Set(), tenantId: null, ready: false };
  }
  if (localStorage.getItem('e2e_skip_session_websocket') !== '1') {
    return { features: new Set(), tenantId: null, ready: false };
  }
  try {
    const raw = localStorage.getItem('session');
    const tenantRaw = localStorage.getItem('currentTenant');
    if (!raw?.trim()) {
      return { features: new Set(), tenantId: null, ready: false };
    }
    const parsed = JSON.parse(raw) as { accessibleFeatureIds?: string[] };
    const tenant = tenantRaw ? (JSON.parse(tenantRaw) as { tenantId?: string }) : null;
    const ids = parsed.accessibleFeatureIds;
    if (!Array.isArray(ids) || !tenant?.tenantId) {
      return { features: new Set(), tenantId: null, ready: false };
    }
    return { features: new Set(ids), tenantId: tenant.tenantId, ready: true };
  } catch {
    return { features: new Set(), tenantId: null, ready: false };
  }
}

const AccessibleFeaturesContext = createContext<UseAccessibleFeaturesResult | null>(null);

/** Session payload may include productization fields from create/validate (avoids separate features request). */
interface SessionWithProductization {
  sessionId?: string;
  accessibleFeatureIds?: string[] | null;
  upgradeCtaUrl?: string | null;
  upgradeCtaLabel?: string | null;
  upgradeCtaEmail?: string | null;
  marketplaceVisibility?: string | null;
}

/**
 * Single source of truth for accessible features (entitlements).
 * Uses session.accessibleFeatureIds when present (from create/validate); otherwise fetches GET /v1/features/accessible.
 */
export function AccessibleFeaturesProvider({ children }: { children: React.ReactNode }) {
  const { session, currentTenant, lastTenantSwitchAt } = useAuth();
  const [accessibleFeatures, setAccessibleFeatures] = useState<Set<string>>(new Set());
  const [upgradeCta, setUpgradeCta] = useState<UpgradeCta>(DEFAULT_UPGRADE_CTA);
  const [marketplaceVisibility, setMarketplaceVisibility] = useState<MarketplaceVisibility | string>(
    'SHOW_ALL_DISABLE_PAID'
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const requestSeqRef = useRef(0);
  const hydratedTenantRef = useRef<string | null>(null);
  const currentTenantId = currentTenant?.tenantId ?? null;

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem('e2e_skip_session_websocket') !== '1') return;
    const boot = readE2eAccessibleFeaturesBootstrap();
    if (!boot.ready) return;
    setAccessibleFeatures(boot.features);
    setIsLoading(false);
    hydratedTenantRef.current = boot.tenantId;
  }, []);

  const fetchFeatures = useCallback(async () => {
    const sessionId = session?.sessionId;
    if (!sessionId) {
      if (
        typeof window !== 'undefined' &&
        localStorage.getItem('e2e_skip_session_websocket') === '1'
      ) {
        const boot = readE2eAccessibleFeaturesBootstrap();
        if (boot.ready) {
          setAccessibleFeatures(boot.features);
          setIsLoading(false);
          hydratedTenantRef.current = boot.tenantId;
          return;
        }
      }
      requestSeqRef.current += 1;
      hydratedTenantRef.current = null;
      setAccessibleFeatures(new Set());
      setUpgradeCta(DEFAULT_UPGRADE_CTA);
      setMarketplaceVisibility('SHOW_ALL_DISABLE_PAID');
      setIsLoading(false);
      setError(null);
      return;
    }
    if (!currentTenantId) {
      // Session is present but tenant context is still hydrating from localStorage/OIDC restore.
      // Keep loading so IntegrationsLayout does not redirect before features are fetched.
      setIsLoading(true);
      return;
    }
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    if (hydratedTenantRef.current !== currentTenantId) {
      // Avoid showing stale feature gates from previously active tenant while refresh is in flight.
      setAccessibleFeatures(new Set());
      setUpgradeCta(DEFAULT_UPGRADE_CTA);
      setMarketplaceVisibility('SHOW_ALL_DISABLE_PAID');
    }
    const sess = session as SessionWithProductization & { accessible_feature_ids?: string[] };
    const idsFromSession =
      Array.isArray(sess.accessibleFeatureIds) && sess.accessibleFeatureIds.length > 0
        ? sess.accessibleFeatureIds
        : Array.isArray(sess.accessible_feature_ids) && sess.accessible_feature_ids.length > 0
          ? sess.accessible_feature_ids
          : null;
    // Use session payload when create/validate included accessibleFeatureIds (avoids flash redirect before API returns).
    const hasTenantScopedSessionFeatures =
      Array.isArray(idsFromSession) && idsFromSession.length > 0;
    if (idsFromSession && hasTenantScopedSessionFeatures) {
      logFeaturesTiming('use session payload');
      if (seq !== requestSeqRef.current) return;
      setAccessibleFeatures(new Set(idsFromSession));
      setUpgradeCta({
        url: sess.upgradeCtaUrl ?? null,
        label: sess.upgradeCtaLabel?.trim() || 'Contact Sales',
        email: sess.upgradeCtaEmail ?? null,
      });
      setMarketplaceVisibility(
        sess.marketplaceVisibility === 'SHOW_FREE_ONLY' ? 'SHOW_FREE_ONLY' : 'SHOW_ALL_DISABLE_PAID'
      );
      setError(null);
      setIsLoading(false);
      hydratedTenantRef.current = currentTenantId;
      return;
    }
    const start = FEATURES_TIMING ? performance.now() : 0;
    logFeaturesTiming('fetch start');
    setIsLoading(true);
    setError(null);
    try {
      const res = await getAccessibleFeatures(sessionId, currentTenantId);
      if (FEATURES_TIMING) {
        const elapsed = (performance.now() - start).toFixed(0);
        console.log(`[features] fetch end @ ${elapsed}ms (backend round-trip)`);
      }
      if (seq !== requestSeqRef.current) return;
      const ids = res.accessibleFeatureIds ?? [];
      setAccessibleFeatures(new Set(ids));
      setUpgradeCta(parseUpgradeCta(res));
      setMarketplaceVisibility(
        res.marketplaceVisibility === 'SHOW_FREE_ONLY' ? 'SHOW_FREE_ONLY' : 'SHOW_ALL_DISABLE_PAID'
      );
      hydratedTenantRef.current = currentTenantId;
    } catch (e) {
      if (seq !== requestSeqRef.current) return;
      setError(e instanceof Error ? e : new Error(String(e)));
      // Keep last known entitlements on transient fetch errors to avoid layout redirects.
      setAccessibleFeatures((prev) => (prev.size > 0 ? prev : new Set()));
      setUpgradeCta(DEFAULT_UPGRADE_CTA);
      setMarketplaceVisibility('SHOW_ALL_DISABLE_PAID');
    } finally {
      if (seq === requestSeqRef.current) {
        setIsLoading(false);
      }
    }
  }, [session, currentTenantId, lastTenantSwitchAt]);

  useEffect(() => {
    logFeaturesTiming('effect run');
    fetchFeatures();
  }, [fetchFeatures]);

  const refetch = useCallback(async () => {
    const sessionId = session?.sessionId;
    if (sessionId && currentTenantId) {
      invalidateAccessibleFeaturesCache({ sessionId, tenantId: currentTenantId });
    }
    await fetchFeatures();
  }, [fetchFeatures, session?.sessionId, currentTenantId]);

  const hasFeature = useCallback(
    (featureId: string) => accessibleFeatures.has(featureId),
    [accessibleFeatures]
  );

  const hasAnyFeature = useCallback(
    (featureIds: string[]) => featureIds.some((id) => accessibleFeatures.has(id)),
    [accessibleFeatures]
  );

  const value = useMemo<UseAccessibleFeaturesResult>(
    () => ({
      accessibleFeatures,
      upgradeCta,
      marketplaceVisibility,
      isLoading,
      error,
      refetch,
      hasFeature,
      hasAnyFeature,
    }),
    [accessibleFeatures, upgradeCta, marketplaceVisibility, isLoading, error, refetch, hasFeature, hasAnyFeature]
  );

  return (
    <AccessibleFeaturesContext.Provider value={value}>
      {children}
    </AccessibleFeaturesContext.Provider>
  );
}

/**
 * Consumes accessible features from AccessibleFeaturesProvider (single fetch, shared cache).
 * Must be used inside AccessibleFeaturesProvider.
 */
export function useAccessibleFeatures(): UseAccessibleFeaturesResult {
  const context = useContext(AccessibleFeaturesContext);
  if (context == null) {
    throw new Error('useAccessibleFeatures must be used within AccessibleFeaturesProvider');
  }
  return context;
}
